import { types, flow } from 'mobx-state-tree'

/**
 *
 * @param {function} config.validationObject function to determine whether a model is saved - input: self. output: bool
 */
const buildCustomValidationStore = (config = {}) => {
  const defaults = {
    validationObject: {},
    asyncValidationObject: {}
  }

  const settings = {
    ...defaults,
    ...config
  }

  const CustomValidation = types
    .model('CustomValidation', {
      // keys touched on step
      dirtyKeys: types.array(types.string),
      // stores errors from attempts to click next
      displayErrors: types.array(
        types.frozen({
          // target: types.string,
          // message: types.string
        })
      )
    })
    .views(self => ({
      get hasDisplayErrors() {
        return self.displayErrors.length > 0
      },
      validateKey(key) {
        if (settings.validationObject[key]) {
          return settings.validationObject[key](self[key])
        }
        return false
      },
      asyncValidateKey: flow(function* (key) {
        const value = self[key]
        const validationFunction = settings.asyncValidationObject[key]
        
        return validationFunction ? yield validationFunction(value) : false
      }),
      validateKeys(keys = []) {
        const hasErrors = keys.reduce((hasError, key) => {
          if (hasError) return hasError
          return self.validateKey(key)
        }, false)
        return hasErrors
      },
      validateDisplayMessage(key) {
        const displayError = self.displayErrors.find(
          item => item.target === key
        )
        const isDirty = self.dirtyKeys.includes(key)
        if (!isDirty && displayError && displayError.message) {
          return displayError.message
        }
        if (isDirty) {
          return self.validateKey(key)
        }
        return false
      }
    }))
    .actions(self => ({
      validateKeyList(keys) {
        const messages = keys.reduce((accu, curr) => {
          const notValid = self.validateKey(curr)
          if (notValid) accu.push({ message: notValid, target: curr })
          return accu
        }, [])

        if (messages.length) {
          self.setDisplayErrors(messages)
          return true
        }
        return false
      },
      asyncValidateKeyList: flow(function* (keys) {
        const messages = []
        
        for (const key of keys) {
          const errorMessage = yield self.asyncValidateKey(key)

          if (errorMessage) messages.push({ message: errorMessage, target: key })
        }
        
        if (!messages.length) return false

        self.addDisplayErrors(messages)
        return true
      }),
      /**
       * Adds a key to dirty keys array
       * @param {string} key The key which to mark as dirty
       */
      addTouched(key) {
        if (self.dirtyKeys && !self.dirtyKeys.includes(key)) {
          self.dirtyKeys.push(key)
        }
      },
      /**
       * Removes a specific key from dirty keys
       * @param {string} key Key which to remove
       */
      removeTouched(key) {
        self.dirtyKeys = self.dirtyKeys.filter(dirtyKey => dirtyKey !== key)
      },
      /**
       * Resets the dirty keys
       */
      resetTouched() {
        self.dirtyKeys = []
      },
      resetDisplayErrors() {
        self.setDisplayErrors([])
      },
      /**
       * Set display errors
       */
      setDisplayErrors(arr) {
        self.displayErrors = arr
        return arr
      },
      addDisplayErrors(arr) {
        return self.setDisplayErrors([...self.displayErrors, ...arr])
      },
      changeKey(key, value) {
        self.addTouched(key)
        self[key] = value
      }
    }))

  return CustomValidation
}

export default buildCustomValidationStore
